import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import io from "socket.io-client";
import styles from "./learningZoneChat.module.css";
import { RiDeleteBinLine, RiKakaoTalkLine } from "react-icons/ri";
import LearningToolModal from "../learningToolModal/LearningToolModal";

const apiUrl = process.env.REACT_APP_URL;

const ChatComponent = ({ currUsername, learningZoneId, isCreatorOrAdmin }) => {
  /* This component is responsible for a chat between users in a learning zone */

  const [messages, setMessages] = useState([]);
  const [qty, setQty] = useState(20);
  const [areThereMoreMessages, setAreThereMoreMessages] = useState(true);
  const [userMsg, setUserMsg] = useState("");
  const [hoveredMsg, setHoveredMsg] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModalMsg, setActiveModalMsg] = useState(null);
  const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);
  const chatAreaRef = useRef(null);
  const prevMsgClickedRef = useRef(false);
  const socket = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    socket.current = io(`${apiUrl}`);

    socket.current.on("receiveMessage", (message) => {
      setMessages((prevMessages) => {
        const existingMessageIds = new Set(
          prevMessages.map((msg) => msg.chat_message_id)
        );
        if (!existingMessageIds.has(message.chat_message_id)) {
          return [message, ...prevMessages];
        }
        return prevMessages;
      });
    });

    return () => {
      socket.current.disconnect();
    };
  }, []);

  /* Get the chat data (previous messages) from the database */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${apiUrl}/chat/chats/${learningZoneId}`, {
          withCredentials: true,
          params: { qty: qty },
        });

        if (res.data) {
          if (res.data.messages.length < qty) {
            setAreThereMoreMessages(false);
          }
          setMessages((prevMessages) => {
            const newMessages = res.data.messages;
            const existingMessageIds = new Set(
              prevMessages.map((msg) => msg.chat_message_id)
            );
            const filteredMessages = newMessages.filter(
              (msg) => !existingMessageIds.has(msg.chat_message_id)
            );
            return [...filteredMessages, ...prevMessages];
          });
        } else {
          console.error("No data received");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [learningZoneId, qty]);

  /* Scroll the chat down, so the last message and the new message input field are visible */
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [messages]);

  /* Handle clicking the Previous Messages button - update qty to get more past messages from the database */
  const handlePrevMsgBtnClick = () => {
    prevMsgClickedRef.current = true;
    setQty((currQty) => currQty + 20);
  };

  /**
   * Handle change in the new message input field
   */
  const handleUserInputChange = (e) => {
    setUserMsg(e.target.value);
  };

  /**
   * Add a new message to the database
   */
  const handleNewMsgBtnClick = async () => {
    try {
      const res = await axios.post(
        `${apiUrl}/chat/chats`,
        { learningZoneId: learningZoneId, content: userMsg },
        {
          withCredentials: true,
        }
      );

      if (res.data) {
        const message = {
          type: "chat",
          username: currUsername,
          content: userMsg,
          timestamp: Date.now(),
          chat_message_id: res.data.chat_message_id,
        };
        socket.current.emit("sendMessage", message);

        setUserMsg("");
      } else {
        console.error("Failed to send message");
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  /**
   * Handle hover over a message
   * @param {*} msgId - the id of the hovered message
   */
  const handleMsgMouseEnter = (msgId) => {
    setHoveredMsg(msgId);
  };

  /**
   * Handle hover out from a message
   */
  const handleMsgMouseLeave = () => {
    setHoveredMsg(null);
  };

  /**
   * Open the confirmation modal to allow the user to delete a message
   * @param {*} msgId - the id of the message to delete
   */
  const handleDeleteMsgBtnClick = (msgId) => {
    setActiveModalMsg(msgId);
    setIsModalOpen(true);
  };

  /**
   * Open the confirmation modal to allow the user to delete all chat messages
   */
  const handleDeleteAllMsgsBtnClick = () => {
    setIsDeleteAllModalOpen(true);
  };

  /**
   * Close the confirmation modal
   */
  const closeModal = () => {
    setIsModalOpen(false);
  };

  /**
   * Close the delete-all confirmation modal
   */
  const closeDeleteAllModal = () => {
    setIsDeleteAllModalOpen(false);
  };

  /**
   * Delete a chat message from the database and remove it from the page display
   */
  const handleModalConfirmMsgDelete = async () => {
    try {
      setMessages((prevMessages) =>
        prevMessages.filter(
          (message) => message.chat_message_id !== activeModalMsg
        )
      );

      const res = await axios.put(
        `${apiUrl}/chat/deactivate_msg/${learningZoneId}/${activeModalMsg}`,
        {},
        {
          withCredentials: true,
        }
      );

      if (res.status !== 200 || !res.data.success) {
        console.error("Error - chat message was not deleted from the DB.");
      }
    } catch (error) {
      console.error("Error - chat message was not deleted from the DB.");
    }

    closeModal();
  };

  /**
   * Delete all chat messages
   */
  const handleDeleteAllModalConfirmMsg = async () => {
    try {
      setMessages([]);

      const res = await axios.put(
        `${apiUrl}/chat/deactivate_all_msgs/${learningZoneId}`,
        {},
        {
          withCredentials: true,
        }
      );

      if (res.status !== 200 || !res.data.success) {
        console.error("Error - chat messages were not deleted from the DB.");
      }
    } catch (error) {
      console.error("Error - chat messages were not deleted from the DB.");
    }

    closeDeleteAllModal();
  };

  /**
   * Convert a timestamp object to a date in the format dd/mm/yyyy
   * @param {} timestamp - a js timestamp object
   * @returns a date in the format dd/mm/yyyy
   */
  function convertTimestampToDate(timestamp) {
    const date = new Date(timestamp);

    const day = date.getUTCDate().toString().padStart(2, "0");
    const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
    const year = date.getUTCFullYear();

    return `${day}/${month}/${year}`;
  }

  /**
   * Prepare the chat messages - provide a relevant message if there are no past messages. If there are, sort the messages in time order (newest appear at the bottom).
   */
  const prepareMessages = () => {
    if (messages.length === 0) {
      return (
        <p className={styles.noMessages}>
          <span className={styles.noMessagesIcon}>
            <RiKakaoTalkLine />
          </span>
          There are no messages yet - be the first and start a conversation!
        </p>
      );
    }

    const reversedMessages = [...messages].reverse();

    let currentDateString = null;

    return reversedMessages.map((msg, index) => {
      const usernameClass = `${
        msg.username === currUsername
          ? styles.currUsername
          : styles.otherUsername
      }`;

      const msgClass = `${
        msg.username === currUsername ? styles.currUserMsg : styles.otherUserMsg
      }`;

      const messageContent = (
        <div
          key={index}
          className={styles.message}
          onMouseEnter={() => handleMsgMouseEnter(msg.chat_message_id)}
          onMouseLeave={handleMsgMouseLeave}
        >
          <button
            className={
              msg.chat_message_id === hoveredMsg && isCreatorOrAdmin
                ? `${styles.visibleButton} ${styles.deleteButton}`
                : `${styles.hiddenButton} ${styles.deleteButton}`
            }
            onClick={() => handleDeleteMsgBtnClick(msg.chat_message_id)}
          >
            <RiDeleteBinLine />
          </button>
          <p>
            <span className={usernameClass}>{msg.username}: </span>
            <span className={`${styles.messageContent} ${msgClass}`}>
              {msg.content}
            </span>
          </p>
        </div>
      );

      const messageDate = convertTimestampToDate(msg.timestamp);
      if (messageDate !== currentDateString) {
        currentDateString = messageDate;
        return (
          <div key={`date-${index}`}>
            <p className={styles.msgDate}>{messageDate}</p>
            {messageContent}
          </div>
        );
      }

      return messageContent;
    });
  };

  // Handle Enter key press to send message
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleNewMsgBtnClick();
    }
  };

  return (
    <div className={styles.chatComponent}>
      <div className={styles.chatHeader}>
        <button
          className={styles.deleteAllMsgsBtn}
          onClick={handleDeleteAllMsgsBtnClick}
        >
          <RiDeleteBinLine />
        </button>
        <div className={styles.titleContainer}>Chat</div>
      </div>
      <div className={styles.chatArea} ref={chatAreaRef}>
        <div className={styles.messagesWrapper}>
          <div className={styles.messagesContainer}>
            {messages.length > 0 && areThereMoreMessages && (
              <button
                className={styles.prevMsgBtn}
                onClick={handlePrevMsgBtnClick}
              >
                Previous Messages
              </button>
            )}
            {prepareMessages()}
          </div>
        </div>
        <div className={styles.inputContainer}>
          <input
            type="text"
            placeholder="Type your message here..."
            className={styles.inputField}
            value={userMsg}
            onChange={handleUserInputChange}
            onKeyDown={handleKeyDown} // Handle Enter key
            ref={inputRef}
          />
          <button className={styles.sendButton} onClick={handleNewMsgBtnClick}>
            Send
          </button>
        </div>
      </div>
      <LearningToolModal
        message="Are you sure you want to delete this message?"
        approveBtnText="YES, DELETE"
        cancelBtnText="No, cancel"
        show={isModalOpen}
        onClose={closeModal}
        onConfirm={handleModalConfirmMsgDelete}
      />
      <LearningToolModal
        message="Are you sure you want to delete ALL messages in this chat?"
        approveBtnText="YES, DELETE"
        cancelBtnText="No, cancel"
        show={isDeleteAllModalOpen}
        onClose={closeDeleteAllModal}
        onConfirm={handleDeleteAllModalConfirmMsg}
      />
    </div>
  );
};

export default ChatComponent;
